import { Effect } from "effect";
import { runtime } from "../services/runtime";
import { ApiClient } from "../services/ApiClient";
import { xmlContentAtom, xslContentAtom, resultContentAtom, lastSavedXmlAtom, lastSavedXslAtom } from "./editor";
import {
  selectedEngineAtom,
  isTransformingAtom,
  isSavingAtom,
  transformStatusAtom,
  saveMessageAtom,
  transformationTimeAtom,
  showHtmlTabAtom,
  showPdfTabAtom,
  showConsoleAtom,
  pdfContentAtom,
  consoleMessagesAtom,
} from "./ui";
import { currentFiddleAtom } from "./fiddle";

// Transform action
export const transformAtom = runtime.fn()(
  Effect.fnUntraced(function* (_, ctx) {
    ctx.set(isTransformingAtom, true);
    ctx.set(transformStatusAtom, "Transforming...");

    const xml = ctx(xmlContentAtom);
    const xsl = ctx(xslContentAtom);
    const engine = ctx(selectedEngineAtom);

    const client = yield* ApiClient;
    const response = yield* Effect.catchAll(
      client.transform.run({ payload: { xml, xsl, engine } }),
      (error) => {
        console.error("Failed to transform:", error);
        return Effect.succeed({ success: false as const, error: String(error) });
      }
    );

    ctx.set(isTransformingAtom, false);

    // Update console messages
    const messages = 'messages' in response && response.messages ? [...response.messages] : [];
    ctx.set(consoleMessagesAtom, messages);
    ctx.set(showConsoleAtom, messages.length > 0);

    if (response.success && response.result !== undefined) {
      ctx.set(resultContentAtom, response.result);
      ctx.set(
        transformStatusAtom,
        `Transformed in ${response.transformationTime ?? 0}ms`
      );
      ctx.set(transformationTimeAtom, response.transformationTime ?? null);

      // Check if result is HTML or XSL-FO
      const isHtml =
        response.result.includes("<html") ||
        response.result.includes("<HTML");
      const isFo = response.result.includes(
        "http://www.w3.org/1999/XSL/Format"
      );
      ctx.set(showHtmlTabAtom, isHtml);
      ctx.set(showPdfTabAtom, isFo);
      ctx.set(pdfContentAtom, null); // Reset PDF when result changes
    } else {
      ctx.set(resultContentAtom, response.error ?? "Transformation failed");
      ctx.set(transformStatusAtom, "Error");
      ctx.set(showHtmlTabAtom, false);
      ctx.set(showPdfTabAtom, false);
    }

    return response;
  })
);

// Save fiddle action
export const saveFiddleAtom = runtime.fn()(
  Effect.fnUntraced(function* (_, ctx) {
    ctx.set(isSavingAtom, true);
    ctx.set(saveMessageAtom, "Saving...");

    const xml = ctx(xmlContentAtom);
    const xsl = ctx(xslContentAtom);
    const engine = ctx(selectedEngineAtom);
    const currentFiddle = ctx(currentFiddleAtom);

    const client = yield* ApiClient;
    const response = yield* Effect.catchAll(
      client.fiddle.save({
        payload: {
          xml,
          xsl,
          engine,
          id_slug: currentFiddle?.id,
        },
      }),
      (error) => {
        console.error("Failed to save fiddle:", error);
        return Effect.succeed({ success: false as const, error: String(error) });
      }
    );

    ctx.set(isSavingAtom, false);

    if (response.success && response.id && response.revision !== undefined) {
      ctx.set(currentFiddleAtom, {
        id: response.id,
        revision: response.revision,
      });
      ctx.set(saveMessageAtom, "Saved!");

      // Update last saved content
      ctx.set(lastSavedXmlAtom, xml);
      ctx.set(lastSavedXslAtom, xsl);

      // Update URL
      const newUrl = `/${response.id}/${response.revision}`;
      window.history.pushState(
        { fiddleId: response.id, revision: response.revision },
        "",
        newUrl
      );

      // Update result if transformation was performed
      if (response.transformation?.success && response.transformation.result) {
        ctx.set(resultContentAtom, response.transformation.result);
        ctx.set(
          transformStatusAtom,
          `Transformed in ${response.transformation.transformationTime ?? 0}ms`
        );

        // Update console messages from transformation
        const messages = response.transformation.messages ? [...response.transformation.messages] : [];
        ctx.set(consoleMessagesAtom, messages);
        ctx.set(showConsoleAtom, messages.length > 0);

        // Check if result is HTML or XSL-FO
        const isHtml =
          response.transformation.result.includes("<html") ||
          response.transformation.result.includes("<HTML");
        const isFo = response.transformation.result.includes(
          "http://www.w3.org/1999/XSL/Format"
        );
        ctx.set(showHtmlTabAtom, isHtml);
        ctx.set(showPdfTabAtom, isFo);
      }

      // Clear message after 2 seconds
      setTimeout(() => {
        ctx.set(saveMessageAtom, "");
      }, 2000);
    } else {
      ctx.set(saveMessageAtom, response.error ?? "Save failed");
    }

    return response;
  })
);

// Generate PDF action
export const generatePdfAtom = runtime.fn()(
  Effect.fnUntraced(function* (_, ctx) {
    const result = ctx(resultContentAtom);
    if (!result.includes("http://www.w3.org/1999/XSL/Format")) {
      return { success: false as const, error: "Not XSL-FO content" };
    }

    const client = yield* ApiClient;
    const response = yield* Effect.catchAll(
      client.pdf.generate({ payload: { xslfo: result } }),
      (error) => {
        console.error("Failed to generate PDF:", error);
        return Effect.succeed({ success: false as const, error: String(error) });
      }
    );

    if (response.success && response.pdfContent) {
      ctx.set(pdfContentAtom, response.pdfContent);
    }

    return response;
  })
);
